'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { knowledgeBaseApi, type SyncTaskSkippedItem } from '../../../_lib/api';

const sourceTypeVariantMap: Record<SyncTaskSkippedItem['sourceType'], 'default' | 'secondary'> = {
  SP_DOCUMENT: 'secondary',
  ARTICLE: 'default',
};

export default function KnowledgeBaseSyncTaskSkippedPage() {
  const { t } = useTranslation();
  const { isReady } = useAuthGuard();
  const router = useRouter();
  const params = useParams();
  const taskId = useMemo(() => {
    const raw = params?.taskId;
    if (Array.isArray(raw)) {
      return raw[0] || '';
    }
    return raw ?? '';
  }, [params]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<SyncTaskSkippedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);

  const fetchSkippedItems = useCallback(async () => {
    if (!taskId) {
      setError(t.knowledgeBase.syncTaskSkipped.taskNotFound);
      setItems([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await knowledgeBaseApi.getSyncTaskSkippedItems(taskId, {
        limit: 200,
        offset: 0,
      });
      setItems(response.items);
      setTotal(response.total);
      setLastCheckedAt(new Date());
    } catch (err) {
      console.error('Failed to fetch skipped items:', err);
      setError(t.knowledgeBase.syncTaskSkipped.loadFailed);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [taskId, t]);

  useEffect(() => {
    if (!isReady) return;
    fetchSkippedItems();
  }, [isReady, fetchSkippedItems]);

  if (!isReady) {
    return (
      <div className="flex h-full items-center justify-center bg-[#f4f5f7]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[#f4f5f7]">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => router.push(`/knowledge-base/sync-tasks/${taskId}`)}
            >
              <ArrowLeft className="h-4 w-4" />
              {t.knowledgeBase.syncTaskSkipped.back}
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                {t.knowledgeBase.syncTaskSkipped.title}
              </h1>
              <p className="text-sm text-gray-500">
                {t.knowledgeBase.syncTaskSkipped.subtitle}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={fetchSkippedItems}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {t.knowledgeBase.syncTaskSkipped.refresh}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <Card className="border-gray-200 shadow-sm">
          <CardHeader className="space-y-2">
            <CardTitle className="text-base font-semibold text-gray-900">
              {t.knowledgeBase.syncTaskSkipped.overviewTitle}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t.knowledgeBase.syncTaskSkipped.totalLabel.replace('{count}', String(total))}
              {lastCheckedAt
                ? ` · ${t.knowledgeBase.syncTaskSkipped.lastChecked.replace('{time}', lastCheckedAt.toLocaleString())}`
                : ''}
            </p>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t.knowledgeBase.syncTaskSkipped.loading}
              </div>
            )}

            {!loading && error && (
              <div className="text-sm text-red-600">{error}</div>
            )}

            {!loading && !error && items.length === 0 && (
              <div className="text-sm text-gray-500">{t.knowledgeBase.syncTaskSkipped.empty}</div>
            )}

            {!loading && items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-[#fafafa] text-xs text-gray-600">
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskSkipped.table.sourceType}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskSkipped.table.filename}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskSkipped.table.fileExtension}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskSkipped.table.mimeType}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskSkipped.table.reason}</th>
                      <th className="px-4 py-3 font-medium">{t.knowledgeBase.syncTaskSkipped.table.createdAt}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Badge variant={sourceTypeVariantMap[item.sourceType]}>
                            {t.knowledgeBase.syncTaskSkipped.sourceType[item.sourceType]}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-700 break-all">{item.filename}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {item.fileExtension ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 break-all">
                          {item.mimeType ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-700 break-all">{item.reason}</td>
                        <td className="px-4 py-3 text-gray-700">
                          {new Date(item.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
